import { Article } from '../models/ArticleResult';
import { CmsCategory } from '@xolv-mybrightlink/common';

export const initialCategoryState = {
  initial: true,
  articles: [],
  endCursor: null,
  error: false,
  loading: false,
};

export type CategoryState = {
  initial: boolean;
  articles: Article[];
  endCursor: string | null;
  error: boolean;
  loading: boolean;
};

type ArticleDetails = {
  databaseId?: number;
  slug?: string;
  data: 'loading' | 'error' | Article;
};

export type ArticleState =
  | {
      initial: true;
    }
  | {
      initial: false;
      categories: CmsCategory[];
      data: Record<string, CategoryState>;
      articleDetails: ArticleDetails[];
    };

export type ArticleAction =
  | { type: 'category-fetched'; categories: CmsCategory[] }
  | { type: 'article-list-fetching'; categoryId: string }
  | {
      type: 'article-list-error';
      categoryId: string;
    }
  | {
      type: 'article-list-fetched';
      categoryId: string;
      articles: Article[];
      endCursor: string | null;
    }
  | { type: 'article-fetching'; databaseId?: number; slug?: string }
  | { type: 'article-error'; databaseId?: number; slug?: string }
  | { type: 'article-fetched'; article: Article }
  | { type: 'bookmark'; databaseId: number };

const isArticleDetails = (
  article: ArticleDetails,
  databaseId: number | undefined,
  slug: string | undefined,
) => {
  return (
    (article.databaseId === databaseId && !!databaseId) ||
    (decodeURI(article.slug ?? '') === decodeURI(slug ?? '') && !!slug)
  );
};

export const findArticleDetails = (
  articles: ArticleDetails[],
  databaseId: number | undefined,
  slug: string | undefined,
) => {
  return articles.find((article) =>
    isArticleDetails(article, databaseId, slug),
  );
};

export const ArticlesReducer = (
  state: ArticleState,
  action: ArticleAction,
): ArticleState => {
  if (action.type === 'category-fetched') {
    return {
      initial: false,
      categories: action.categories,
      data: {},
      articleDetails: [],
    };
  }
  if (state.initial) {
    return state;
  }
  if (action.type === 'bookmark') {
    const data = Object.entries(state.data).reduce((result, entry) => {
      const [categoryId, categoryState] = entry;
      const articles = categoryState.articles.map((article) => {
        if (article.databaseId === action.databaseId) {
          return { ...article, isSaved: !article.isSaved };
        }
        return article;
      });
      result[categoryId] = { ...categoryState, articles };
      return result;
    }, {} as Record<string, CategoryState>);
    const articleDetails = state.articleDetails.map((article) => {
      if (isArticleDetails(article, action.databaseId, undefined)) {
        const data =
          typeof article.data === 'string'
            ? article.data
            : { ...article.data, isSaved: !article.data.isSaved };
        return { ...article, data };
      }
      return article;
    });
    return { ...state, data, articleDetails };
  } else if (action.type === 'article-list-fetching') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: { ...categoryState, loading: true },
      },
    };
  } else if (action.type === 'article-list-error') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: { ...categoryState, error: true },
      },
    };
  } else if (action.type === 'article-list-fetched') {
    const categoryId = action.categoryId;
    const categoryState = state.data[categoryId] ?? initialCategoryState;
    return {
      ...state,
      data: {
        ...state.data,
        [action.categoryId]: {
          ...categoryState,
          initial: false,
          loading: false,
          articles: [...categoryState.articles, ...action.articles],
          endCursor: action.endCursor,
        },
      },
    };
  } else if (action.type === 'article-fetching') {
    const article = findArticleDetails(
      state.articleDetails,
      action.databaseId,
      action.slug,
    );
    if (!article) {
      return {
        ...state,
        articleDetails: [
          ...state.articleDetails,
          { databaseId: action.databaseId, slug: action.slug, data: 'loading' },
        ],
      };
    }
  } else if (action.type === 'article-error') {
    return {
      ...state,
      articleDetails: state.articleDetails.map((article) => {
        if (isArticleDetails(article, action.databaseId, action.slug)) {
          return { ...article, data: 'error' };
        }
        return article;
      }),
    };
  } else if (action.type === 'article-fetched') {
    const fetchedArticle = action.article;
    return {
      ...state,
      articleDetails: state.articleDetails.map((article) => {
        if (
          isArticleDetails(
            article,
            fetchedArticle.databaseId,
            fetchedArticle.slug,
          )
        ) {
          return {
            databaseId: fetchedArticle.databaseId,
            slug: fetchedArticle.slug,
            data: action.article,
          };
        }
        return article;
      }),
    };
  }
  return state;
};
